import type { UserKpiEntry } from "@nexus/contracts"
import {
  COLUMN_FIELDS,
  compileKpiFormula,
  evalKpi,
  KpiFormulaError,
  type CompiledKpiFormula,
  type KpiNumericRow,
} from "@nexus/kpi-formula"
import { formatUserKpiValue } from "@/features/kpi-settings/lib/user-kpi-model"
import type { UserKpiCellValue } from "@/features/kpi-settings/components/UserKpiGrid"
import type { UserKpiTone } from "@/features/kpi-settings/components/UserKpiBox"
import {
  PERFORMANCE_ENTRY_BY_KPI_NAME,
  PERFORMANCE_ENTRY_BY_SQL_SUFFIX,
  type PerformanceKpiCatalogEntry,
} from "./my-brand-drawer-performance-catalog"
import {
  performanceRowValues,
  performanceTrend,
  type PerformanceTrendTone,
} from "./my-brand-drawer-performance-model"

/** The drawer's own trend tones map onto the shared box tones. */
const TONE_BY_TREND: Record<Exclude<PerformanceTrendTone, null>, UserKpiTone> = {
  positive: "green",
  negative: "red",
  flat: "neutral",
}

const EMPTY_CELL = (emptyValue: string): UserKpiCellValue => ({
  value: emptyValue,
  badgeText: null,
  tone: "neutral",
})

/**
 * Display values for the My Brand creative drawer, keyed by `UserKpiEntry.name`.
 *
 * Canonical catalog names (CPA, Spend, …) read pre-aggregated `current_*` /
 * `baseline_*` columns so Retool-parity badge math stays intact. Every other
 * row — user-created UUID names, seed tokens like `Link Clicks` — compiles its
 * formula against the same row's raw `COLUMN_FIELDS` aggregates.
 */
export function buildDrawerPerformanceKpiCells(
  entries: UserKpiEntry[],
  row: Record<string, unknown> | null | undefined,
  options: { currencySymbol?: string; emptyValue?: string } = {},
): Record<string, UserKpiCellValue> {
  const { currencySymbol = "$", emptyValue = "—" } = options
  const cells: Record<string, UserKpiCellValue> = {}
  const source = row ?? {}

  for (const entry of entries) {
    const field = PERFORMANCE_ENTRY_BY_KPI_NAME[entry.name]
    cells[entry.name] = field
      ? cellFromCatalogColumn(entry, field, source, currencySymbol, emptyValue)
      : cellFromFormula(entry, source, currencySymbol, emptyValue)
  }

  return cells
}

function cellFromCatalogColumn(
  entry: UserKpiEntry,
  field: PerformanceKpiCatalogEntry,
  row: Record<string, unknown>,
  currencySymbol: string,
  emptyValue: string,
): UserKpiCellValue {
  const { current, baseline } = performanceRowValues(row, field.sqlSuffix)
  const { trendLabel, trendTone } = performanceTrend(catalogTrend(entry, field), current, baseline)

  return {
    value: formatUserKpiValue(current, entry.format, entry.decimals, currencySymbol, emptyValue),
    badgeText: trendLabel,
    tone: trendTone ? TONE_BY_TREND[trendTone] : "neutral",
  }
}

/**
 * The catalog entry's fixed badge direction, except Custom (cost per custom event): it follows
 * the tenant's KPI polarity — POSITIVE → lower is better, NEGATIVE → higher is better — so the
 * cell agrees with the Why tab. Without a polarity it keeps the catalog direction.
 */
function catalogTrend(
  entry: UserKpiEntry,
  field: PerformanceKpiCatalogEntry,
): Pick<PerformanceKpiCatalogEntry, "comparison" | "betterDirection"> {
  if (field.id !== "custom") return field
  if (entry.polarity === "POSITIVE") return { comparison: field.comparison, betterDirection: "low" }
  if (entry.polarity === "NEGATIVE") return { comparison: field.comparison, betterDirection: "high" }
  return field
}

function cellFromFormula(
  entry: UserKpiEntry,
  row: Record<string, unknown>,
  currencySymbol: string,
  emptyValue: string,
): UserKpiCellValue {
  const expr = entry.formula?.trim() || entry.name
  try {
    const ast = compileKpiFormula(expr)
    const currentRow = kpiNumericFromPerformanceRow(row, "current")
    const baselineRow = kpiNumericFromPerformanceRow(row, "baseline")
    const current = evalKpi(ast, currentRow, baselineRow)
    const baseline = evalKpi(ast, baselineRow, baselineRow)
    const { trendLabel, trendTone } = performanceTrend(formulaTrend(entry, ast), current, baseline)
    return {
      value: formatUserKpiValue(current, entry.format, entry.decimals, currencySymbol, emptyValue),
      badgeText: trendLabel,
      tone: trendTone ? TONE_BY_TREND[trendTone] : "neutral",
    }
  } catch (error) {
    if (error instanceof KpiFormulaError) return EMPTY_CELL(emptyValue)
    throw error
  }
}

/**
 * Single-column formulas reuse the Figma catalog's share/offset rules when the
 * token is a catalog `sqlSuffix`. Catalog `none` (Frequency) still offsets here
 * so custom formulas can use `baseline_frequency`. SUM and averages with no
 * catalog cell stay on the formula-only path.
 */
function formulaTrend(
  entry: UserKpiEntry,
  ast: CompiledKpiFormula,
): Pick<PerformanceKpiCatalogEntry, "comparison" | "betterDirection"> {
  if (astUsesSum(ast)) {
    return { comparison: "none", betterDirection: "neutral" }
  }
  if (ast.kind === "col") {
    const catalog = PERFORMANCE_ENTRY_BY_SQL_SUFFIX[ast.field]
    if (catalog && catalog.comparison !== "none") {
      return { comparison: catalog.comparison, betterDirection: catalog.betterDirection }
    }
    if (entry.format === "NUMBER" && ast.field !== "frequency" && ast.field !== "avg_watch_time") {
      return { comparison: "share", betterDirection: "neutral" }
    }
  }
  return {
    comparison: "offset",
    betterDirection: entry.polarity === "POSITIVE" ? "low" : "high",
  }
}

function astUsesSum(ast: CompiledKpiFormula): boolean {
  switch (ast.kind) {
    case "sum":
      return true
    case "bin":
      return astUsesSum(ast.left) || astUsesSum(ast.right)
    default:
      return false
  }
}

function kpiNumericFromPerformanceRow(
  row: Record<string, unknown>,
  which: "current" | "baseline",
): KpiNumericRow {
  const out: KpiNumericRow = {}
  for (const field of COLUMN_FIELDS) {
    const values = performanceRowValues(row, field)
    const n = values[which]
    if (n !== null) out[field] = n
  }
  return out
}
